import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '../utils/cn';
import { ToastContext } from './ToastContext';
import type { Toast, ToastType } from './ToastContext';

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts((prev) => [...prev, { id, message, type }]);

        setTimeout(() => {
            setToasts((prev) => prev.filter((toast) => toast.id !== id));
        }, 5000);
    }, []);

    const removeToast = (id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {createPortal(
                <div className="fixed bottom-24 left-1/2 z-[9999] w-full max-w-[90vw] -translate-x-1/2 space-y-2 pointer-events-none md:bottom-8 md:max-w-sm">
                    {toasts.map((toast) => (
                        <div
                            key={toast.id}
                            className={cn(
                                "flex items-center justify-between rounded-xl p-4 shadow-2xl pointer-events-auto transition-all duration-300",
                                toast.type === 'success' && "bg-green-600 text-white",
                                toast.type === 'error' && "bg-red-600 text-white",
                                toast.type === 'info' && "bg-bg-card-alt text-text-heading border border-border-default"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                {toast.type === 'success' && <CheckCircle size={20} />}
                                {toast.type === 'error' && <AlertCircle size={20} />}
                                {toast.type === 'info' && <Info size={20} />}
                                <p className="text-sm font-bold leading-tight">{toast.message}</p>
                            </div>
                            <button
                                onClick={() => { removeToast(toast.id); }}
                                className="ml-2 flex-shrink-0 rounded-full p-1 hover:bg-white/20 transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </ToastContext.Provider>
    );
};
